import { ApolloError } from '@apollo/client';
import moment from 'moment';

import type {
  ErpAiAgentOverviewQuery,
  ErpAiAgentRunQuery,
  ErpAiAgentRunsQuery,
} from 'gqlhooks';

import { formatAiScore, formatAiShare } from './aiQualityModel';

/**
 * ТЗ 0104, §4 «Платформа» — чистая модель вкладок «Przegląd» и
 * «Uruchomienia»: статусы и источники прогона, коды отказов, деньги,
 * токены, длительность, метрики агента и расход по дням.
 *
 * Без React — тестируется напрямую. Импортируется прямым путём
 * (`core/hooks/AiAgents/aiAgentPlatformModel`), не через барабан
 * `core/hooks`: там имена потоков столкнулись бы.
 */

// ── Типы ответа ────────────────────────────────────────────────────────────

export type AiAgentOverviewData = ErpAiAgentOverviewQuery['aiAgentOverview'];
export type AiAgentInfo = AiAgentOverviewData['agents'][number];
export type AiCostDay = AiAgentOverviewData['costByDay'][number];
export type AiAgentRunPage = ErpAiAgentRunsQuery['aiAgentRuns'];
export type AiAgentRunRow = AiAgentRunPage['rows'][number];
export type AiAgentRunDetail = NonNullable<ErpAiAgentRunQuery['aiAgentRun']>;
export type AiAgentRunStep = AiAgentRunDetail['steps'][number];

// ── Словари строк сервера (graphene-енумов нет — строки) ───────────────────

/** Агенты платформы — порядок карточек «Przeglądu», если сервер их отдаст. */
export const AI_AGENT_KEYS = [
  'slack_watch',
  'reply_draft',
  'student_portrait',
  'lesson_analysis',
  'chat',
] as const;
export type AiAgentKey = typeof AI_AGENT_KEYS[number];

export const isAiAgentKey = (value: unknown): value is AiAgentKey =>
  (AI_AGENT_KEYS as readonly unknown[]).includes(value);

export const AI_AGENT_RUN_STATUSES = [
  'queued',
  'running',
  'waiting_human',
  'done',
  'failed',
  'cancelled',
  'skipped',
] as const;
export type AiAgentRunStatus = typeof AI_AGENT_RUN_STATUSES[number];

export const isAiAgentRunStatus = (value: unknown): value is AiAgentRunStatus =>
  (AI_AGENT_RUN_STATUSES as readonly unknown[]).includes(value);

/** Статусы шага прогона — подмножество статусов прогона. */
export const AI_AGENT_STEP_STATUSES = [
  'running',
  'done',
  'failed',
  'skipped',
] as const;

export const AI_AGENT_TRIGGERS = [
  'beat',
  'signal',
  'manual',
  'chat',
  'backfill',
  'eval',
] as const;
export type AiAgentTrigger = typeof AI_AGENT_TRIGGERS[number];

export const isAiAgentTrigger = (value: unknown): value is AiAgentTrigger =>
  (AI_AGENT_TRIGGERS as readonly unknown[]).includes(value);

/**
 * Прогон «в движении»: только по ним идёт опрос (список — раз в 5 с,
 * открытый прогон — раз в 3 с). `waiting_human` ждёт человека часами —
 * опрашивать его незачем.
 */
export const AI_AGENT_POLLED_STATUSES: readonly string[] = [
  'queued',
  'running',
];

export const aiAgentRunPolled = (status: string | null | undefined): boolean =>
  Boolean(status) && AI_AGENT_POLLED_STATUSES.includes(status as string);

/**
 * Прогон ещё «живой» — его можно отменить. Зеркало `ACTIVE_STATUSES`
 * бэкенда: завершённый сервер вернёт как есть.
 */
export const AI_AGENT_CANCELABLE_STATUSES: readonly string[] = [
  'queued',
  'running',
  'waiting_human',
];

export const aiAgentRunCancelable = (
  status: string | null | undefined,
): boolean =>
  Boolean(status) && AI_AGENT_CANCELABLE_STATUSES.includes(status as string);

/** Провайдеры с человеческим именем (`aiAgents.overview.provider.<id>`). */
export const AI_AGENT_PROVIDERS = ['deepseek', 'anthropic', 'replay'] as const;

export const isAiAgentProvider = (value: unknown): boolean =>
  (AI_AGENT_PROVIDERS as readonly unknown[]).includes(value);

/**
 * Почему агент выключен (`offReason`): `disabled_on_server` — сервер, стенд
 * или env (в ERP не включить), `disabled` — выключен в «Настройки → ИИ».
 */
export const AI_AGENT_OFF_REASONS = ['disabled_on_server', 'disabled'] as const;
export type AiAgentOffReason = typeof AI_AGENT_OFF_REASONS[number];

export const isAiAgentOffReason = (value: unknown): value is AiAgentOffReason =>
  (AI_AGENT_OFF_REASONS as readonly unknown[]).includes(value);

/**
 * Коды отказов (`extensions.code`) запуска и отмены — у каждого своя фраза
 * `aiAgents.runs.refusal.<код>`; незнакомый — общая.
 */
export const AI_AGENT_REFUSALS = [
  'ai_disabled',
  'budget_exceeded',
  'already_running',
  'not_found',
  'bad_input',
  'forbidden',
] as const;
export type AiAgentRefusal = typeof AI_AGENT_REFUSALS[number];

export const isAiAgentRefusal = (value: unknown): value is AiAgentRefusal =>
  (AI_AGENT_REFUSALS as readonly unknown[]).includes(value);

/** `extensions.code` первой кодированной ошибки GraphQL, иначе `null`. */
export const aiAgentErrorCode = (error: unknown): string | null => {
  if (!(error instanceof ApolloError)) return null;
  const coded = (error.graphQLErrors ?? []).find(
    (item) => typeof item?.extensions?.code === 'string',
  );
  return coded ? String(coded.extensions?.code) : null;
};

// ── Числа ──────────────────────────────────────────────────────────────────

/**
 * Числа — как деньги остальных экранов ERP (`_detailShared/format`): польская
 * запись при любом языке интерфейса, «1 234,56». Форматтеры дорогие —
 * создаются один раз.
 */
const USD_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const USD_SMALL_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const INT_FMT = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 });
const ONE_DECIMAL_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const finite = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Стоимость в долларах: «12,34 USD». Прогон обычно стоит доли цента —
 * «0,00 USD» у ненулевого прогона было бы враньём, поэтому меньше цента —
 * четыре знака: «0,0042 USD».
 */
export const formatAiUsd = (value: number | null | undefined): string => {
  const num = finite(value);
  if (num === null) return '—';
  const abs = Math.abs(num);
  const fmt = abs > 0 && abs < 0.01 ? USD_SMALL_FMT : USD_FMT;
  return `${fmt.format(num)} USD`;
};

/** Целое с разрядами: «12 345». */
export const formatAiCount = (value: number | null | undefined): string => {
  const num = finite(value);
  return num === null ? '—' : INT_FMT.format(num);
};

/** Доля бюджета, 0…∞ %; бюджета нет (или ноль) — `null`. */
export const aiBudgetPercent = (
  cost: number | null | undefined,
  budget: number | null | undefined,
): number | null => {
  const limit = finite(budget);
  if (limit === null || limit <= 0) return null;
  return ((finite(cost) ?? 0) / limit) * 100;
};

/** Тон полосы бюджета: с 80 % — жёлтый, с 100 % — красный. */
export const aiBudgetTone = (
  percent: number | null,
): 'ok' | 'warn' | 'danger' => {
  if (percent === null) return 'ok';
  if (percent >= 100) return 'danger';
  if (percent >= 80) return 'warn';
  return 'ok';
};

// ── Время ──────────────────────────────────────────────────────────────────

/** Дата и время, как на остальных экранах ERP: «09.10.2026 14:05». */
export const formatAiDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : '—';
};

/** Время шага (внутри одного прогона дата одна): «14:05:12». */
export const formatAiTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('HH:mm:ss') : '—';
};

/**
 * Длительность в мс: своя у прогона (`durationMs`), иначе из начала и
 * конца; шаг без конца — `null` (ещё идёт).
 */
export const aiDurationMs = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  explicit?: number | null,
): number | null => {
  const own = finite(explicit);
  if (own !== null) return own;
  if (!startedAt || !finishedAt) return null;
  const ms = moment(finishedAt).diff(moment(startedAt));
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
};

/**
 * Длительность — части для перевода: `ms` («850 ms»), `seconds`
 * («12,3 s»), `minutes` («2 min 05 s»). Подписи единиц — в локалях.
 */
export type AiDurationParts =
  | { unit: 'ms'; value: string }
  | { unit: 'seconds'; value: string }
  | { unit: 'minutes'; min: string; sec: string };

export const aiDurationParts = (ms: number | null): AiDurationParts | null => {
  const num = finite(ms);
  if (num === null || num < 0) return null;
  if (num < 1000) return { unit: 'ms', value: INT_FMT.format(num) };
  if (num < 60000) {
    return { unit: 'seconds', value: ONE_DECIMAL_FMT.format(num / 1000) };
  }
  const totalSec = Math.round(num / 1000);
  return {
    unit: 'minutes',
    min: INT_FMT.format(Math.floor(totalSec / 60)),
    sec: String(totalSec % 60).padStart(2, '0'),
  };
};

// ── Метрики агента ─────────────────────────────────────────────────────────

export interface IAiAgentMetric {
  key: string;
  /** Уже отформатированное значение. */
  value: string;
}

export interface IAiAgentMetrics {
  /** Сервер не смог посчитать метрики (`{"error": "metrics_failed"}`). */
  failed: boolean;
  items: IAiAgentMetric[];
}

/** Сколько метрик показывает карточка агента, которого экран не знает. */
export const AI_AGENT_METRICS_SHOWN = 3;

/**
 * Служебные ключи `metrics()` — не метрики: окно подсчёта (`period`,
 * `since`) и признак сбоя (`error`). На карточке их не показываем никогда.
 */
export const AI_AGENT_METRIC_SERVICE_KEYS: readonly string[] = [
  'period',
  'since',
  'error',
];

/**
 * Запись значения: `count` — целое («12»), `share` — доля 0…1 процентами
 * («82,5%»), `usd` — доллары («0,0042 USD»), `score` — балл с одним знаком
 * («72,5»).
 */
export type AiAgentMetricFormat = 'count' | 'share' | 'usd' | 'score';

export interface IAiAgentMetricSpec {
  key: string;
  format: AiAgentMetricFormat;
}

/**
 * Метрики карточки по агентам — осмысленные, а не «первые три ключа».
 * Ключи — те, что отдают `metrics()` бэкенда (`ai_agents/slack/services/
 * metrics.py`, `replies/services.py`, `portrait/services.py`,
 * `lessons/metrics.py`, `chat/services.py`); подписи —
 * `aiAgents.overview.metric.<key>`.
 */
export const AI_AGENT_CARD_METRICS: Readonly<
  Record<AiAgentKey, readonly IAiAgentMetricSpec[]>
> = {
  slack_watch: [
    { key: 'findings', format: 'count' },
    { key: 'precision', format: 'share' },
    { key: 'false_positive', format: 'count' },
  ],
  reply_draft: [
    { key: 'proposed', format: 'count' },
    { key: 'insert_rate', format: 'share' },
    { key: 'sent_rate', format: 'share' },
    // Нормированное расстояние правки 0…1 — как на вкладке «Jakość».
    { key: 'median_edit_distance', format: 'share' },
  ],
  student_portrait: [
    { key: 'portraits_generated', format: 'count' },
    { key: 'avg_cost_usd', format: 'usd' },
    { key: 'stale_share', format: 'share' },
  ],
  lesson_analysis: [
    { key: 'analyses_done', format: 'count' },
    { key: 'human_review_share', format: 'share' },
    { key: 'avg_overall_score', format: 'score' },
  ],
  chat: [
    { key: 'threads', format: 'count' },
    { key: 'user_messages', format: 'count' },
  ],
};

/**
 * Запись метрики незнакомого агента — по имени ключа: доли
 * (`*_rate`, `*_share`, `precision`) — процентами, `*_usd` — долларами,
 * остальное — числом.
 */
export const aiAgentMetricFormatOf = (key: string): AiAgentMetricFormat => {
  if (/(^|_)(rate|share)$/.test(key) || key === 'precision') return 'share';
  if (/(^|_)usd$/.test(key)) return 'usd';
  return 'score';
};

/** Значение метрики по её записи; `null` и мусор — «—». */
export const formatAiAgentMetric = (
  value: unknown,
  format: AiAgentMetricFormat,
): string => {
  // Только число или числовая строка: `Number([])` — это 0, а не «нет».
  const num =
    typeof value === 'number' || typeof value === 'string'
      ? finite(value)
      : null;
  if (num === null) return '—';
  if (format === 'count') return formatAiCount(num);
  if (format === 'share') return formatAiShare(num);
  if (format === 'usd') return formatAiUsd(num);
  return formatAiScore(num);
};

const formatFallbackValue = (key: string, value: unknown): string | null => {
  if (typeof value === 'boolean') return value ? '✓' : '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatAiAgentMetric(value, aiAgentMetricFormatOf(key));
  }
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
};

/**
 * `metrics` — `JSONString` (строка с JSON-объектом; `{}` — метрик нет).
 * Знакомый агент (`AI_AGENT_CARD_METRICS`) — ровно его метрики в его
 * порядке; ключа нет или он `null` — «—» (метрика есть, значения пока
 * нет). Незнакомый — первые несколько простых значений без служебных
 * ключей; вложенные объекты и списки пропускаются.
 */
export const parseAiAgentMetrics = (
  raw: unknown,
  agentKey: string | null = null,
  limit: number = AI_AGENT_METRICS_SHOWN,
): IAiAgentMetrics => {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { failed: false, items: [] };
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { failed: false, items: [] };
  }
  const values = value as Record<string, unknown>;
  const entries = Object.entries(values);
  const failed = typeof values.error === 'string';
  const meaningful = entries.filter(
    ([key]) => !AI_AGENT_METRIC_SERVICE_KEYS.includes(key),
  );
  if (meaningful.length === 0) return { failed, items: [] };

  if (isAiAgentKey(agentKey)) {
    // Сбой подсчёта — без строки «—» на каждую метрику: значений нет.
    if (failed) return { failed, items: [] };
    return {
      failed,
      items: AI_AGENT_CARD_METRICS[agentKey].map(({ key, format }) => ({
        key,
        value: formatAiAgentMetric(values[key], format),
      })),
    };
  }

  const items = meaningful
    .map(([key, item]) => {
      const text = formatFallbackValue(key, item);
      return text === null ? null : { key, value: text };
    })
    .filter((item): item is IAiAgentMetric => item !== null)
    .slice(0, limit);
  return { failed, items };
};

/**
 * Подпись метрики, у которой нет перевода: `edit_distance` → «Edit
 * distance». Перевод, если поток агента его заведёт, — ключом
 * `aiAgents.overview.metric.<key>`.
 */
export const humanizeAiMetricKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
};

// ── Расход по дням ─────────────────────────────────────────────────────────

/** Окно графика расхода — как у сервера (`costByDay`, 30 дней). */
export const AI_COST_DAYS = 30;

export interface IAiCostSeries {
  agentKey: string;
  /** Стоимость по дням оси, USD; дня без вызовов — 0. */
  data: number[];
  total: number;
}

export interface IAiCostChart {
  /** Дни оси, `YYYY-MM-DD`, от старого к сегодняшнему. */
  days: string[];
  series: IAiCostSeries[];
  total: number;
}

/**
 * Расход по дням для столбцов «по агентам»: ось — последние 30 дней до
 * `today` включительно (дни без вызовов — нули, а не дыры), ряд — агент.
 * Порядок рядов — порядок карточек (`agentOrder`), незнакомые ключи — в
 * конце; ряд без единого цента не рисуется.
 */
export const buildAiCostChart = (
  rows: ReadonlyArray<Pick<AiCostDay, 'day' | 'agentKey' | 'costUsd'>>,
  agentOrder: ReadonlyArray<string>,
  today: moment.Moment = moment(),
  days: number = AI_COST_DAYS,
): IAiCostChart => {
  const end = today.clone().startOf('day');
  const axis = Array.from({ length: days }, (_, index) =>
    end
      .clone()
      .subtract(days - 1 - index, 'days')
      .format('YYYY-MM-DD'),
  );
  const position = new Map(axis.map((day, index) => [day, index]));
  const byAgent = new Map<string, number[]>();
  rows.forEach((row) => {
    const index = position.get(String(row.day).slice(0, 10));
    const cost = finite(row.costUsd) ?? 0;
    if (index === undefined || cost === 0) return;
    const key = row.agentKey || '';
    const data = byAgent.get(key) ?? axis.map(() => 0);
    data[index] += cost;
    byAgent.set(key, data);
  });
  const rank = (key: string): number => {
    const at = agentOrder.indexOf(key);
    return at === -1 ? agentOrder.length : at;
  };
  const series = Array.from(byAgent.entries())
    .map(([agentKey, data]) => ({
      agentKey,
      data,
      total: data.reduce((sum, item) => sum + item, 0),
    }))
    .sort(
      (a, b) =>
        rank(a.agentKey) - rank(b.agentKey) ||
        a.agentKey.localeCompare(b.agentKey),
    );
  return {
    days: axis,
    series,
    total: series.reduce((sum, item) => sum + item.total, 0),
  };
};

// ── Фильтры журнала ────────────────────────────────────────────────────────

/** Строк на странице журнала прогонов. */
export const AI_AGENT_RUNS_PAGE_SIZE = 50;

/** Параметры адреса вкладки «Uruchomienia». */
export const AI_AGENT_RUNS_PARAMS = {
  run: 'run',
  agent: 'agent',
  status: 'status',
  trigger: 'trigger',
  offset: 'offset',
} as const;

/** Смещение страницы из адреса: не число или меньше нуля — первая. */
export const parseAiRunsOffset = (raw: string | null): number => {
  const num = Number(raw);
  return Number.isInteger(num) && num > 0 ? num : 0;
};

/**
 * Подпись объекта прогона: имя от сервера, иначе «вид #id», иначе `null`
 * (прогон без объекта — «по всему агенту»).
 */
export const aiRunSubjectText = (
  run: Pick<AiAgentRunRow, 'subjectLabel' | 'subjectKind' | 'subjectId'>,
): string | null => {
  const label = (run.subjectLabel ?? '').trim();
  if (label) return label;
  if (run.subjectKind && run.subjectId) {
    return `${run.subjectKind} #${run.subjectId}`;
  }
  if (run.subjectId) return `#${run.subjectId}`;
  return null;
};
